import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';

import generateId from 'src/lib/generateId';
import { BuildInMemoryTypes } from 'src/models/Sidekick/MemoryTypes';
import { getModuleSpec } from 'src/presentation/components/pages/sidekick/threads/utils/utils';
import { SidekickCustomer } from 'src/models/Sidekick/SidekickCustomer';
import SidekickModuleSpec, {
  CONTROL_TYPE_TO_DEFAULT_VALUE,
  ControlSpec,
  ControlValue,
  ModifierSpec,
  RuleSet,
  Type,
  TypeSpec,
  VCustomerIntegration,
} from 'src/models/Sidekick/SidekickModuleSpec';
import { ModifierValue, NextData } from 'src/models/Sidekick/SidekickThreads';

export const createNewModule = (
  name: string,
  specs: Record<string, SidekickModuleSpec>,
  integrations: Record<string, VCustomerIntegration[]>
) => {
  const spec = specs[name];
  if (spec == null) {
    throw new Error('null module spec');
  }
  const data: NextData = {
    id: generateId(),
    btResource: spec.btResource,
    controlValues: {} as Record<string, ControlValue>,
    externalAction: spec.externalAction,
    integrations: {} as Record<string, number>,
    modifierValues: [] as ModifierValue[],
    name: name,
  };
  if (spec == null) {
    throw new Error('null spec');
  }

  setDefaultValues(data, spec);

  const integrationsMap: Record<string, string> | null = getSpecIntegrations(spec, integrations);
  data.integrations = integrationsMap || {};

  return data;
};

export const defaultModifierControls = (foundModifier: ModifierSpec, modVal: ModifierValue) => {
  setDefaultValues(modVal, foundModifier);
  return modVal;
};
const getSpecIntegrations = (spec: SidekickModuleSpec, integrations: Record<string, VCustomerIntegration[]>) => {
  if (isUndefined(spec.integrations) || spec.integrations === null) {
    return null;
  }

  return spec.integrations
    .filter(impl => integrations[impl] && integrations[impl].length > 0 && integrations[impl][0])
    .reduce((acc, c) => ({ ...acc, [c]: integrations[c][0].customerIntegrationID }), {});
};

const setDefaultValues = <T extends ModifierValue | NextData, M extends ModifierSpec | SidekickModuleSpec>(
  value: T,
  spec: M
) => {
  const controls = allControlSpecs(spec);

  for (const control of controls) {
    if (control.defaultValue != null) {
      value.controlValues[control.name] = control.defaultValue;
      continue;
    }

    if (CONTROL_TYPE_TO_DEFAULT_VALUE[control.type] === undefined) {
      throw new Error(`unknown control type default value: ${control.type}`);
    }

    value.controlValues[control.name] = CONTROL_TYPE_TO_DEFAULT_VALUE[control.type];
  }
};

const allControlSpecs = (modSpec: ModifierSpec): ControlSpec[] => {
  if (modSpec.controlCategories == null) {
    return [];
  }
  const out: ControlSpec[] = [];
  for (const category of modSpec.controlCategories) {
    for (const group of category.controlGroups) {
      for (const control of group.controls) {
        out.push(control);
      }
    }
  }
  return out;
};

export enum NodesTypes {
  MODULE,
  MODIFIER,
}

// TODO: move to types...
export interface NestedModuleOrModifier {
  attr?: string;
  inRule: boolean;
  mod: NextData | ModifierValue;
  nodeType: NodesTypes;
  spec?: SidekickModuleSpec | ModifierSpec;
}

// TODO: This function is mutating the types object. It should be refactored to return a new object.
export const definedMemoriesAndExActionNames = (
  mods: NestedModuleOrModifier[],
  flowName: string,
  customer: SidekickCustomer,
  moduleSpecs: Record<string, SidekickModuleSpec>,
  types: Record<string, TypeSpec>
) => {
  const extActionMemNames: string[] = [];

  const memoryTypes = cloneDeep(BuildInMemoryTypes);

  if (customer?.useConversations === false) {
    memoryTypes['ticket'] = 'Ticket';
  }

  // TODO: refactor this it should not be flow name dependent
  if (flowName.replaceAll(' ', '').toLocaleLowerCase().trim() === 'transferflow') {
    memoryTypes['transferReason'] = 'string';
    memoryTypes['isHandoff'] = 'bool';
  }

  if (mods.length === 0) {
    return [memoryTypes, []];
  }

  for (const nm of mods) {
    let spec: SidekickModuleSpec | ModifierSpec | undefined = moduleSpecs[nm.mod.name];
    if (spec == null) {
      // It's probably a modifier.
      for (const moduleState of Object.values(moduleSpecs)) {
        const module = moduleState;
        const moduleSpec = moduleSpecs[module.name];
        if (moduleSpec == null) {
          throw new Error(`null module spec: ${module.name}`);
        }
        if (moduleSpec.modifiers == null) {
          continue;
        }
        for (const modifierSpec of moduleSpec.modifiers) {
          if (modifierSpec.name === nm.mod.name) {
            spec = modifierSpec;
            break;
          }
        }
        if (spec != null) {
          break;
        }
      }
    }
    if (spec == null) {
      continue;
    }
    let currentMod = nm.mod;
    let isRule = false;

    // This check needs to occur before the external action and filter
    // data checks below, since it updates the spec to the action
    // within the anon modifier. If this happens after those other
    // checks, memories from within the anon modifier will not be
    // available in the thread.
    if (spec.name === 'rule') {
      // Rules only have out memories when they have an anon module
      // that outputs them.
      const tmpRule = nm?.mod?.controlValues['rule'] as RuleSet;
      if (tmpRule == null) {
        continue;
      }
      const am = tmpRule.then.anonymousModule;
      if (am == null) {
        continue;
      }
      currentMod = am;
      spec = moduleSpecs[am.name];
      if (spec == null) {
        continue;
      }
      isRule = true;
    }

    if ((spec as SidekickModuleSpec)?.externalAction?.results != null) {
      const cur = currentMod as NextData;
      for (const res of cur.externalAction?.results || []) {
        const memName = String(cur.controlValues['resultMemoryName']);
        if (memName == null) {
          continue;
        }

        // Ext actions in rules are discovered twice. Once within
        // the anon modifier, and once as the modifier of Rules.
        // We only need to add it to this list once.
        if (isRule) {
          if (extActionMemNames.indexOf(memName) === -1) {
            extActionMemNames.push(memName);
            memoryTypes[memName] = res.name;
          }
          continue;
        }
        extActionMemNames.push(memName);
        memoryTypes[memName] = res.name;
      }
    }
    if (spec.name === 'filterData') {
      const filterArray = (currentMod as NextData).controlValues['data'] as string[];
      const [name, typeSpec] = findTypeForFilterData(types, memoryTypes, filterArray);
      if (name != null && typeSpec != null) {
        // Use the first index of the filter array to determine whether it's an
        // external action because the output of filtering might be a nested
        // array that isn't defined as an external action memory.
        const isExtAction = extActionMemNames.indexOf(filterArray[0] || '') !== -1;
        const singularName = getSingularNameForObject(name, isExtAction);
        addTypeForNameAndSpec(name, singularName, typeSpec, types);

        // Confirm memoryTypes maintains case-insensitive uniqueness.
        if (!caseInsensitiveKeyInRecord(singularName, memoryTypes)) {
          // Create a singular version of the memory if the
          // filtered memory.
          memoryTypes[singularName] = singularName;
        }
        if (!caseInsensitiveKeyInRecord(name, memoryTypes)) {
          memoryTypes[name] = name;
        }
      }
    }

    for (const memName in spec.outMemories) {
      const memType = spec.outMemories[memName];
      if (memType == null) {
        continue;
      }
      memoryTypes[memName] = memType;
    }
  }
  return [memoryTypes, extActionMemNames];
};

// TODO: this method is mutating the types object. It should be refactored to return a new object.
// TODO: refactor it....
const addTypeForNameAndSpec = (
  name: string,
  singularName: string,
  spec: TypeSpec,
  types: Record<string, TypeSpec>
): void => {
  if (spec == null || name == null || singularName == null) {
    return;
  }

  // The spec is in props.items.items in the case of FNArays.
  const typProps = types[spec.type]?.props || {};
  const specItems = spec.items || typProps['items']?.items;
  if (specItems != null && !caseInsensitiveKeyInRecord(singularName, types)) {
    types[singularName] = specItems;
  }

  // Confirm types maintains case-insensitive uniqueness.
  if (caseInsensitiveKeyInRecord(name, types)) {
    return;
  }
  types[name] = spec;
};

const caseInsensitiveKeyInRecord = (target: string, rec: Record<string, unknown>): boolean => {
  const lower = target.toLowerCase();
  for (const key of Object.keys(rec)) {
    if (key.toLowerCase() == lower) {
      return true;
    }
  }
  return false;
};

const findTypeForFilterData = (
  types: Record<string, TypeSpec>,
  mems: Record<string, string>,
  fields: string[]
): [string?, TypeSpec?] => {
  if (fields == null || fields.length == 0) {
    return [];
  }
  const object = fields[0];
  if (object == null || object == '') {
    return [];
  }
  fields = fields.slice(1);
  let data = mems[object];
  if (data == null) {
    return [];
  }
  let ts = types[data];
  if (ts == null) {
    return [];
  }
  if (ts.type == Type.array) {
    return [object, ts];
  }
  let props: Record<string, TypeSpec> | undefined = ts.props;
  while (fields.length > 0) {
    if (fields.length == 1 && fields[0] === 'items') {
      return [data, ts];
    }
    data = fields[0];
    if (data == null) {
      return [];
    }
    if (!builtin(ts.type)) {
      ts = types[ts.type];
      if (ts == null) {
        return [];
      }
      props = ts.props;
    }
    if (props == null) {
      return [];
    }
    ts = props[data];
    if (ts == null) {
      return [];
    }
    props = ts.props;
    fields = fields.slice(1);
  }
  return [data, ts];
};

// TODO: move to types...
export const builtin = (type: Type) => {
  return Object.values(Type).includes(type as Type);
};

const getSingularNameForObject = (pluralObj: string, extAction: boolean): string => {
  if (!extAction && pluralObj[pluralObj.length - 1] === 's') {
    return pluralObj.slice(0, pluralObj.length - 1);
  }
  return pluralObj + '_first_';
};

export const getNestedModules = (
  moduleIndex: number,
  modifierIndex: number,
  modules: NextData[],
  moduleSpecsMap: Record<string, SidekickModuleSpec>
) => {
  const nestedMods: NestedModuleOrModifier[] = [];
  const mods = modules.slice(0, moduleIndex + 1);
  for (const [i, mod] of Object.entries(mods)) {
    nestedMods.push({ inRule: false, mod, nodeType: NodesTypes.MODULE });
    const spec = moduleSpecsMap[mod.name];

    if (spec == null) {
      continue;
    }

    if (Number(i) === moduleIndex && (modifierIndex === -1 || modifierIndex === null)) {
      continue;
    }

    let modifiers = mod.modifierValues || [];
    if (Number(i) === modifierIndex && Number(i) === moduleIndex) {
      modifiers = modifiers.slice(0, modifierIndex + 1);
    }

    for (const modifier of modifiers) {
      nestedMods.push({ inRule: false, mod: modifier, nodeType: NodesTypes.MODIFIER });

      if (modifier.name !== 'rule') {
        continue;
      }

      const tmp = modifier.controlValues['rule'];
      if (tmp == null) {
        continue;
      }
      const ruleSet = tmp as RuleSet;
      if (ruleSet.then.anonymousModule == null) {
        continue;
      }
      const ruleSteps = flattenSteps([ruleSet.then.anonymousModule]).map(rs => ({
        ...rs,
        inRule: true,
      }));

      nestedMods.push(...ruleSteps);
    }
  }
  return nestedMods;
};

export const flattenSteps = (
  modules: NextData[],
  attr: string = '',
  specsMap?: Record<string, SidekickModuleSpec>
): NestedModuleOrModifier[] => {
  // Be sure to record any rules' anonymous module memories as well. We
  // can't prove that these memories are always defined, since they're set
  // conditionally in Threads, but we need to default to permissive in cases
  // of ambiguity and trust users to make the right choice -- we can only
  // highlight undefined memory errors in cases where we know that
  // something can never work, e.g. using "Track Shipment" before "Look Up
  // Order".
  const flatten = (module: NextData, attr): NestedModuleOrModifier[] => {
    const spec = (specsMap ? getModuleSpec(module, specsMap) : { modifiers: [] }) as SidekickModuleSpec;
    const out: NestedModuleOrModifier[] = [{ inRule: false, mod: module, attr, spec, nodeType: NodesTypes.MODULE }];

    forEach(module.modifierValues, (modifier, index) => {
      const modifierSpec = spec?.modifiers?.find(m => m.name === modifier.name);
      out.push({
        inRule: false,
        mod: modifier,
        attr: `${attr}.modifierValues[${index}]`,
        spec: modifierSpec,
        nodeType: NodesTypes.MODIFIER,
      });

      if (modifier.name !== 'rule') {
        return;
      }
      const tmp = modifier.controlValues['rule'];
      if (tmp == null) {
        return;
      }
      const ruleSet = tmp as RuleSet;
      if (ruleSet.then.anonymousModule == null) {
        return;
      }
      const ruleSteps = map(
        flatten(
          ruleSet.then.anonymousModule,
          `${attr}.modifierValues[${index}].controlValues.rule.then.anonymousModule`
        ),
        rs => {
          return { ...rs, inRule: true };
        }
      );
      out.push(...ruleSteps);
    });
    return out;
  };
  const out: NestedModuleOrModifier[] = [];

  forEach(modules, (module, index) => {
    out.push(...flatten(module, `${attr}[${index}]`));
  });

  return out;
};
